import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, PapiUser } from '@wix/da-types-papi';
import { MeasuredCookieType } from '@wix/da-shared-react/pkg/Measured';
import DeviationTorpedo from '@wix/da-shared-react/pkg/Torpedo/DeviationTorpedo';
import ConditionalWrapper from '@wix/da-shared-react/pkg/ConditionalWrapper';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import { BiEventType } from '@wix/da-shared-react/pkg/biLogger/constants';
import DuperbrowseContext from '../../../contexts/DuperbrowseContext';
import { hashNumbers } from '../../../utils/hash';
import {
  ExhibitionistViewMode,
  ExhibitionistPremiumFolderData,
  ExhibitionistSlideFitMode,
} from '../../../../types/modules';
import FolderPaywall from '../_partials/paywalls/FolderPaywall';
import DeviationPaywall from '../_partials/paywalls/DeviationPaywall';
import {
  GallectionType,
  GallectionResourceType,
} from '../../../../types/gallection';
import DeviationSlideshow from './DeviationSlideshow';

import s from './TallExhibitionist.scss';

export interface Props {
  sourceType: GallectionType;
  viewMode: ExhibitionistViewMode;
  slideImageFit: ExhibitionistSlideFitMode;
  streamId: string;
  deviations: PapiDeviation[];
  seeAllUrl: string;
  profileOwnerUser: PapiUser;
  premiumFolderData?: ExhibitionistPremiumFolderData;
  folderId: number;
}

// DeviationTorpedo configuration params
const container = { width: 450, maxRows: 3 };
const defaultSize = {
  desktop: { width: 450, height: 0 },
  mobile: { width: 375, height: 0 },
};

export const TallExhibitionist: React.FC<Props> = ({
  deviations,
  profileOwnerUser,
  premiumFolderData,
  streamId,
  sourceType,
  folderId,
  viewMode,
  slideImageFit,
}) => {
  const { t } = useTranslation();
  const isCollection = sourceType === GallectionType.COLLECTION;

  const num = hashNumbers(
    ...deviations.map(deviation => deviation.deviationId)
  );

  const PaywallComponent =
    viewMode === ExhibitionistViewMode.SLIDESHOW
      ? DeviationPaywall
      : FolderPaywall;

  const biTypeId = isCollection
    ? GallectionResourceType.COLLECTION
    : GallectionResourceType.GALLERY;

  const { pointPrice } = normalizePremiumFolderData(premiumFolderData);

  return (
    <div className={s['root']} key={num.toString()}>
      <ConditionalWrapper
        condition={!isCollection}
        wrap={child => (
          <PaywallComponent
            premiumFolderData={premiumFolderData}
            contentOwner={profileOwnerUser}
            folderId={folderId}
            biData={{
              evid: BiEventType.GALLERY_PURCHASE,
              points: pointPrice,
              typeid: biTypeId,
              itemid: folderId,
              galleryid: folderId,
              authorid: profileOwnerUser.useridUuid,
            }}
          >
            {child}
          </PaywallComponent>
        )}
      >
        <DuperbrowseContext
          streamId={streamId}
          deviations={deviations}
          parent={{ title: t('duperbrowse.backButton.profile') }}
        >
          {viewMode === ExhibitionistViewMode.SLIDESHOW ? (
            <DeviationSlideshow
              items={deviations}
              useCoverMode={slideImageFit === ExhibitionistSlideFitMode.COVER}
            />
          ) : (
            <DeviationTorpedo
              items={deviations}
              container={container}
              cookieType={MeasuredCookieType.USER_PROFILE_FEATURED}
              cookieDim={'width'}
              defaultSize={defaultSize}
              hideIncompleteRows
              enableScrollVisibilityOptimisation
            />
          )}
        </DuperbrowseContext>
      </ConditionalWrapper>
    </div>
  );
};
TallExhibitionist.displayName = 'TallExhibitionist';

export default TallExhibitionist;
